import { useState } from 'react';
import { Image, Pressable, Text, View } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import type { AbaId, Quadro as Q, Traco } from '../store/types';
import { Sheet } from './ui';
import { C, modoDoApp } from './theme';
import { FolhaFundo } from './FolhaFundo';
import { fonteImagem } from './Anexos';
import { corNaFolha, folhaEscura, fundoFolha } from '../lib/paginas';
import { Quadro, LARG_BASE, spansDoTexto } from './Quadro';
import { t } from '../lib/i18n';
import { medidasTexto, yAssentado } from '../lib/textoFolha';

// Desenho à mão livre com o dedo ou com caneta (S Pen, Apple Pencil, caneta de tablet).
// As coordenadas ficam numa área de 1000 de largura (a largura da tela). Desde a 0.47 a área cresce
// para baixo e para a direita (larg), como um quadro infinito.

/** Mostra um desenho salvo (só leitura), inteiro, na largura disponível. quadro (0.65): com textos, checklists, tabelas e imagens. */
export function DesenhoView({ tracos, alt, larg = LARG_BASE, fundo, quadro }: { tracos: Traco[]; alt: number; larg?: number; fundo?: string; quadro?: Q | null }) {
  const [w, setW] = useState(0);
  const escuroApp = modoDoApp() === 'escuro';
  const folha = quadro?.folha ?? null;
  const bg = folha ? fundoFolha(folha, escuroApp) : fundo ?? C.card2;
  const escura = folha ? folhaEscura(folha, escuroApp) : escuroApp;
  const tinta = (c?: string) => corNaFolha(c ?? '#111827', escura);
  const L = Math.max(larg, quadro?.larg ? Math.min(quadro.larg, larg) : larg), A = alt;
  const s = w / L;
  return (
    <View onLayout={e => setW(e.nativeEvent.layout.width)} style={{ width: '100%', aspectRatio: L / A, borderRadius: 12, overflow: 'hidden', backgroundColor: bg }}>
      {w > 0 && (
        <>
          <Svg width={w} height={(w * A) / L} viewBox={`0 0 ${L} ${A}`} style={{ position: 'absolute' }}>
            {!!folha && <FolhaFundo folha={folha} larg={L} alt={A} fundo={bg} escura={escura} idBase={'v' + Math.round(A)} />}
            {(quadro?.tracos ?? tracos).map((tr, i) => <Path key={i} d={tr.d} stroke={tinta(tr.cor)} strokeWidth={tr.w} fill="none" strokeLinecap="round" strokeLinejoin="round" />)}
          </Svg>
          {quadro?.imagens?.map(im => { const src = fonteImagem(im.arq); return src ? <Image key={im.id} source={{ uri: src }} resizeMode="contain" style={{ position: 'absolute', left: im.x * s, top: im.y * s, width: im.w * s, height: im.h * s }} /> : null; })}
          {quadro?.textos.filter(x => x.texto.trim()).map(x => (
            <Text key={x.id} allowFontScaling={false} style={{ position: 'absolute', left: x.x * s + 4, top: yAssentado(x, folha) * s, width: x.w * s, color: tinta(x.cor),
              fontSize: Math.max(5, medidasTexto(x).f * s), lineHeight: Math.max(6, medidasTexto(x).l * s),
              fontWeight: x.b ? '800' : '400', fontStyle: x.i ? 'italic' : 'normal', textDecorationLine: x.u ? 'underline' : 'none' }}>{spansDoTexto(x, s, c => tinta(c))}</Text>
          ))}
          {quadro?.checks.map(c => (
            <View key={c.id} style={{ position: 'absolute', left: c.x * s, top: c.y * s, width: c.w * s }}>
              {c.itens.filter(i => i.text.trim()).map(i => <Text key={i.id} style={{ color: tinta(), fontSize: Math.max(5, 36 * s), textDecorationLine: i.done ? 'line-through' : 'none' }}>{i.done ? '☑' : '☐'} {i.text}</Text>)}
            </View>
          ))}
          {quadro?.tabelas?.map(tb => (
            <View key={tb.id} style={{ position: 'absolute', left: tb.x * s, top: tb.y * s, width: tb.w * s }}>
              {tb.linhas.map((l, r) => <View key={r} style={{ flexDirection: 'row' }}>{l.map((v, c) => <Text key={c} style={{ flex: 1, borderWidth: 0.5, borderColor: C.line, color: tinta(), fontSize: Math.max(5, (tb.tams?.[`${r}-${c}`] ?? 34) * (30 / 34) * s), padding: 2 }}>{v}</Text>)}</View>)}
            </View>
          ))}
        </>
      )}
    </View>
  );
}

/** Área usada pelo quadro (com folga), para o desenho salvo não ficar com espaço vazio enorme. */
export function areaUsada(q: Q, altMin: number) {
  let mx = 0, my = 0;
  for (const tr of q.tracos) for (const m of tr.d.matchAll(/[ML](-?[\d.]+) (-?[\d.]+)/g)) { mx = Math.max(mx, +m[1]); my = Math.max(my, +m[2]); }
  for (const x of [...q.textos.filter(y => y.texto.trim()), ...q.checks, ...(q.tabelas ?? [])]) { mx = Math.max(mx, x.x + x.w); my = Math.max(my, x.y + 120); }
  for (const im of q.imagens ?? []) { mx = Math.max(mx, im.x + im.w); my = Math.max(my, im.y + im.h); }
  return { alt: Math.max(altMin, Math.ceil(my + 60)), larg: Math.max(LARG_BASE, Math.ceil(mx + 60)) };
}

/** Tela de desenho (0.65: as mesmas ferramentas do quadro das páginas). Ao tocar em "Salvar", devolve os traços,
 *  o tamanho e o quadro inteiro (com textos, checklists, tabelas e imagens). */
export function DesenhoSheet({ visible, inicial, inicialQ, alt = 700, larg = LARG_BASE, cor, onClose, onSalvar, assinatura, aba }: {
  visible: boolean; inicial?: Traco[]; inicialQ?: Q | null; alt?: number; larg?: number; cor: string; onClose: () => void;
  onSalvar: (tracos: Traco[], alt: number, larg: number, quadro: Q | null) => void;
  assinatura?: boolean;   // assinatura (Scanner, PDF Edit): só caneta e borracha, folha lisa
  aba?: AbaId;
}) {
  const novo = (): Q => (inicialQ ? { ...inicialQ, larg: Math.max(inicialQ.larg, LARG_BASE), alt: Math.max(inicialQ.alt, 1400) }
    : { tracos: inicial ?? [], textos: [], checks: [], larg: Math.max(larg, LARG_BASE), alt: Math.max(alt, 1400) });
  const [q, setQ] = useState<Q>(novo);
  const [aberto, setAberto] = useState(visible);
  if (visible !== aberto) { setAberto(visible); if (visible) setQ(novo()); }
  const salvar = () => {
    const a = areaUsada(q, alt);
    const soTracos = !q.textos.some(x => x.texto.trim()) && !q.checks.length && !q.tabelas?.length && !q.imagens?.length && !q.folha;
    onSalvar(q.tracos, a.alt, a.larg, assinatura || soTracos ? null : q);
  };
  return (
    <Sheet visible={visible} onClose={onClose} title={assinatura ? t('Assinatura') : t('Desenho')}
      right={<Pressable onPress={salvar}><Text style={{ color: cor, fontWeight: '800' }}>{t('Salvar')}</Text></Pressable>}>
      <View style={{ flex: 1 }}>
        <Quadro valor={q} onChange={setQ} cor={cor} aba={aba} simples={assinatura} modoInicial={assinatura ? 'caneta' : undefined} telaCheia nome={t('Desenho')} />
      </View>
    </Sheet>
  );
}
